import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from "react-router-dom";
import {
    IconLayoutDashboard,
    IconServer,
    IconWorld,
    IconGlobe,
    IconCertificate,
    IconUsers,
    IconCreditCard,
    IconRoute2,
    IconHistory,
    IconLogout,
    IconMessageCircle,
} from '@tabler/icons-react';

import { useAuth } from "./../../AuthContext";
import { PUBLIC_URL, PRODUCT_ID } from './../../const';
import resolveUserRole from "../utils/resolveUserRole";
import { fetchAwaitingUserCount } from "../../services/supportTickets";
import { fetchPendingInvoicesCount } from "../../services/invoices";
import LanguageSwitcher from "../core/switch-language";

const dict = {
    en: {
        dashboard: "Dashboard",
        servers: "Servers",
        websites: "Web Sites",
        domains: "Domains",
        certificates: "Certificates",
        contactsGroup: "Contacts Group",
        invoices: "Invoices",
        users: "Users",
        history: "Change History",
        tickets: "Support Tickets",
        logoutAria: "logout",
    },
    ru: {
        dashboard: "Панель",
        servers: "Серверы",
        websites: "Сайты",
        domains: "Домены",
        certificates: "Сертификаты",
        contactsGroup: "Группы контактов",
        invoices: "Счета",
        users: "Пользователи",
        history: "История изменений",
        tickets: "Тикеты поддержки",
        logoutAria: "выйти из аккаунта",
    },
    ua: {
        dashboard: "Панель",
        servers: "Сервери",
        websites: "Веб-сайти",
        domains: "Домени",
        certificates: "Сертифікати",
        contactsGroup: "Групи контактів",
        invoices: "Рахунки",
        users: "Користувачі",
        history: "Історія змін",
        tickets: "Тікети підтримки",
        logoutAria: "вийти з акаунта",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const resolveWorkspaceId = (user = {}) => {
    const storedCompanyId = localStorage.getItem("companyId");
    const id =
        user?.current_workspace_id ||
        user?.workspace_id ||
        user?.selected_workspace_id ||
        user?.workspace?.id ||
        user?.company_id ||
        storedCompanyId;
    if (!id) return null;
    const parsed = parseInt(id, 10);
    return Number.isNaN(parsed) ? id : parsed;
};

const LeftMenu = ({ lang: langProp, setLang }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const [currentLang, setCurrentLang] = useState(getInitialLang());
    const lang = (langProp || currentLang);
    const safeSetLang = typeof setLang === "function" ? setLang : () => {};
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const userRole = useMemo(() => resolveUserRole(user), [user]);
    const hideInvoices = userRole === "member";
    const hideHistory = userRole === "member";
    const [ticketsAwaitingCount, setTicketsAwaitingCount] = useState(0);
    const [invoicePendingCount, setInvoicePendingCount] = useState(0);

    const refreshTicketsCount = async () => {
        if (!user?.token) {
            setTicketsAwaitingCount(0);
            return;
        }
        const lang = (localStorage.getItem("lang") || "en").toLowerCase();
        const count = await fetchAwaitingUserCount(user.token, resolveWorkspaceId(user), lang);
        setTicketsAwaitingCount(count);
    };

    useEffect(() => {
        const handleLangChange = (event) => {
            const next = (event?.detail?.lang || localStorage.getItem("lang") || "en").toLowerCase();
            setCurrentLang(next);
        };
        window.addEventListener("app:languageChanged", handleLangChange);
        window.addEventListener("storage", handleLangChange);
        return () => {
            window.removeEventListener("app:languageChanged", handleLangChange);
            window.removeEventListener("storage", handleLangChange);
        };
    }, []);

    useEffect(() => {
        let intervalId;
        refreshTicketsCount();
        intervalId = setInterval(refreshTicketsCount, 5 * 60 * 1000);

        const handleCounterUpdate = (event) => {
            const next = event.detail?.count;
            if (typeof next === "number") {
                setTicketsAwaitingCount(next);
            } else {
                refreshTicketsCount();
            }
        };

        window.addEventListener("supportTicketsCounterUpdate", handleCounterUpdate);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener("supportTicketsCounterUpdate", handleCounterUpdate);
        };
    }, [user?.token, user?.company_id, user?.current_workspace_id, user?.workspace_id]);

    useEffect(() => {
        if (!user?.token || hideInvoices) {
            setInvoicePendingCount(0);
            return undefined;
        }

        let cancelled = false;

        const run = async () => {
            const count = await fetchPendingInvoicesCount({
                token: user.token,
                productId: PRODUCT_ID,
                workspaceId: resolveWorkspaceId(user),
                lang: (localStorage.getItem("lang") || "en").toLowerCase(),
            });
            if (!cancelled) {
                setInvoicePendingCount(count);
            }
        };

        run();
        const intervalId = setInterval(run, 5 * 60 * 1000);

        const handleInvoiceCountersUpdate = (event) => {
            if (cancelled) return;
            if (typeof event?.detail?.pending_count === "number") {
                setInvoicePendingCount(event.detail.pending_count);
            } else {
                run();
            }
        };

        window.addEventListener("billing:invoices:refreshCounters", handleInvoiceCountersUpdate);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            window.removeEventListener("billing:invoices:refreshCounters", handleInvoiceCountersUpdate);
        };
    }, [user?.token, user?.company_id, user?.current_workspace_id, user?.workspace_id, hideInvoices]);

    const menuItems = useMemo(() => {
        let baseItems = [
            { path: "/dashboard", label: t.dashboard, icon: IconLayoutDashboard },
            { path: "/servers", label: t.servers, icon: IconServer },
            { path: "/web-sites", label: t.websites, icon: IconWorld },
            { path: "/domains", label: t.domains, icon: IconGlobe },
            { path: "/certificates", label: t.certificates, icon: IconCertificate },
            { path: "/contacts-group", label: t.contactsGroup, icon: IconRoute2 },
            { path: "/history", label: t.history, icon: IconHistory },
            { path: "/tickets", label: t.tickets, icon: IconMessageCircle },
            { path: "/invoices", label: t.invoices, icon: IconCreditCard },
            { path: "/users", label: t.users, icon: IconUsers },
        ];
        if (hideInvoices) {
            baseItems = baseItems.filter((item) => item.path !== "/invoices");
        }
        if (hideHistory) {
            baseItems = baseItems.filter((item) => item.path !== "/history");
        }
        return baseItems;
    }, [t, hideInvoices, hideHistory]);

    return (
        <>
            <div className="brand-logo d-flex align-items-center justify-content-between">
                <a href="/dashboard" className="text-nowrap logo-img">
                    <img
                        src="https://my-sites-guard.com/assets/img/home_logo.png"
                        className="dark-logo"
                        alt="Logo-Dark"
                        style={{ maxHeight: "40px" }}
                    />
                </a>
                <button
                    className="sidebartoggler sidebar-close-btn d-block d-xl-none rounded-circle border-0"
                    type="button"
                    onClick={() => {
                        const wrapper = document.getElementById("main-wrapper");
                        if (wrapper) {
                            wrapper.classList.remove("show-sidebar");
                            window.dispatchEvent(new CustomEvent("sidebar:toggled", { detail: { isOpen: false } }));
                        }
                    }}
                    style={{
                        width: "48px",
                        height: "48px",
                        minWidth: "48px",
                        marginLeft: "40px",
                        backgroundColor: "var(--bs-body-bg)",
                        boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    aria-label="Close sidebar"
                >
                    <i className="ti ti-x fs-5 text-dark"></i>
                </button>
            </div>

            <div className="d-xl-none w-100 px-4 mt-2 mb-1">
                <LanguageSwitcher
                    lang={lang}
                    setLang={safeSetLang}
                    style={{ marginBottom: "10px", width: "100%", justifyContent: "center" }}
                />
            </div>

            <nav className="sidebar-nav scroll-sidebar" data-simplebar>
                <ul id="sidebarnav">
                    {menuItems.map(({ path, label, icon: IconComponent }) => {
                        const isTickets = path === "/tickets";
                        const isInvoices = path === "/invoices";
                        const badge = (() => {
                            if (isTickets && ticketsAwaitingCount > 0) {
                                return ticketsAwaitingCount > 99 ? "99+" : ticketsAwaitingCount;
                            }
                            if (isInvoices && invoicePendingCount > 0) {
                                return invoicePendingCount > 99 ? "99+" : invoicePendingCount;
                            }
                            return null;
                        })();
                        return (
                            <li className="sidebar-item" key={path}>
                                <Link
                                    to={path}
                                    className={`sidebar-link d-flex align-items-center justify-content-between ${location.pathname === path ? "active" : ""}`}
                                    data-text={label}
                                    onClick={() => {
                                        if (isTickets) {
                                            window.dispatchEvent(new CustomEvent("supportTicketsCounterUpdate", { detail: { refresh: true } }));
                                        }
                                        if (isInvoices) {
                                            window.dispatchEvent(new CustomEvent("billing:invoices:refreshCounters", { detail: { refresh: true } }));
                                        }
                                        const wrapper = document.getElementById("main-wrapper");
                                        if (wrapper) {
                                            wrapper.classList.remove("show-sidebar");
                                            window.dispatchEvent(new CustomEvent("sidebar:toggled", { detail: { isOpen: false } }));
                                        }
                                    }}
                                >
                                    <span className="d-flex align-items-center gap-2">
                                        <IconComponent size={18} />
                                        <span className="hide-menu">{label}</span>
                                    </span>
                                    {badge && (
                                        <span className="badge rounded-pill bg-danger-subtle text-danger ms-2">
                                            {badge}
                                        </span>
                                    )}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <div className="fixed-profile p-3 mx-4 mb-2 bg-secondary-subtle rounded mt-3">
                <div className="hstack gap-3">
                    <div className="john-img">
                        <img
                            src={`${PUBLIC_URL}/assets/images/profile/user-1.jpg`}
                            className="rounded-circle"
                            width="40"
                            height="40"
                            alt=""
                        />
                    </div>
                    <div className="john-title">
                        <h6 className="mb-0 fs-4 fw-semibold">
                            {user.first_name} {user.last_name}
                        </h6>
                    </div>
                    <button
                        className="border-0 bg-transparent text-primary ms-auto"
                        tabIndex="0"
                        type="button"
                        aria-label={t.logoutAria}
                        onClick={logout}
                    >
                        <IconLogout size={18} />
                    </button>
                </div>
            </div>
        </>
    );
};

export default LeftMenu;
